import type { TReminderFormValues } from './schema';
import { useFormikContext, ErrorMessage } from 'formik';
import { useEffect, useMemo } from 'react';
import { InfoCircle } from 'iconsax-react';

import { Alert, AlertDescription } from '@src/design-system/alert';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { ProgramStageMultiSelect } from './program-stage-multi-select';
import { ProgramQuestionSelect } from './program-question-select';
import { REMINDER_TRIGGER_TYPES } from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { Textfield } from '@src/shared/fields/text-field';
import { SelectField } from '@src/shared/fields/select-field';
import { useParams } from 'react-router-dom';
import stageApi from '@src/features/programs/pages/create/services/stage-api';
import programFieldApi from '@src/features/programs/pages/create/services/program-field-api';
import { cn } from '@src/design-system/helpers';

export const ProgramRelatedFields = () => {
  const { id: programId = '' } = useParams();

  const { data: stagesData } = stageApi.useFetch({ program: programId });
  const stages = stagesData?.results || [];

  const { values, setFieldValue, setFieldTouched, setTouched } =
    useFormikContext<TReminderFormValues>();

  const { data: fieldsData } = programFieldApi(programId).useFetch();
  const dateFields = fieldsData?.results.filter((f) => f.type === 'DATE') || [];

  const frequency =
    values.frequency == null || String(values.frequency) === ''
      ? 0
      : Number(values.frequency);
  const selectedQuestion = dateFields.find(
    (field) => String(field.id) === String(values.question)
  );
  const questionName = selectedQuestion?.title
    ? String(selectedQuestion.title)
    : 'Question Date';

  useEffect(() => {
    if (!values.trigger_type) {
      setFieldValue('trigger_type', REMINDER_TRIGGER_TYPES.before, false);
    }
  }, [setFieldValue, values.trigger_type]);

  const handleFrequencyBlur = () => {
    setFieldTouched('frequency', true);
    if (values.frequency == null || String(values.frequency) === '') {
      setFieldValue('frequency', 0, false);
    }
  };

  const alertMessage = useMemo(() => {
    if (values.trigger_type === REMINDER_TRIGGER_TYPES.on) {
      return (
        <>
          Send this reminder on the{' '}
          <span className='font-bold'>{questionName}</span>.
        </>
      );
    }

    if (values.trigger_type === REMINDER_TRIGGER_TYPES.after) {
      return (
        <>
          Send this reminder after{' '}
          <span className='font-bold'>{frequency}</span> days of the{' '}
          <span className='font-bold'>{questionName}</span>.
        </>
      );
    }

    return (
      <>
        Send this reminder before <span className='font-bold'>{frequency}</span>{' '}
        days of the <span className='font-bold'>{questionName}</span>.
      </>
    );
  }, [frequency, questionName, values.trigger_type]);

  const shouldShowAlert = Boolean(values.trigger_type);

  return (
    <div className='flex flex-col gap-4'>
      <ProgramStageMultiSelect stages={stages} />

      <div className='grid grid-cols-4 gap-4'>
        <div>
          <SelectField
            selectName='trigger_type'
            selectPlaceholder='Select'
            onValueChange={(value) => {
              setTouched({ frequency: false });
              setFieldValue('frequency', 0);
              setFieldValue('trigger_type', value);
            }}
            options={Object.values(REMINDER_TRIGGER_TYPES).map((t) => ({
              label: formatTextForDisplay(t),
              value: t,
            }))}
          />
        </div>

        <div className='col-span-2 flex flex-col'>
          <ProgramQuestionSelect dateFields={dateFields} />
        </div>

        <div className='overflow-visible'>
          <div className='flex items-start'>
            <div className='w-[60px] shrink-0'>
              <Textfield
                type='number'
                disabled={values.trigger_type === REMINDER_TRIGGER_TYPES.on}
                name='frequency'
                placeholder='Enter days'
                hideError
                className='rounded-r-none'
                onBlur={handleFrequencyBlur}
              />
            </div>
            <div
              className={cn(
                'flex h-12 shrink-0 items-center justify-center bg-primary-100 text-gray-800 text-sm font-medium rounded-r-lg px-3',
                values.trigger_type === REMINDER_TRIGGER_TYPES.on &&
                  'opacity-70'
              )}
            >
              Days
            </div>
          </div>
          <ErrorMessage
            name='frequency'
            component={CustomErrorMessage}
            className='whitespace-nowrap'
          />
        </div>
      </div>
      {shouldShowAlert ? (
        <Alert className='border-0 bg-primary-100 text-primary-1000'>
          <AlertDescription className='text-primary-1000 flex items-center gap-1 text-sm'>
            <span>
              <InfoCircle size={20} />
            </span>
            {alertMessage}
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
};
